import { GET_USER_PRICE_ALERTS, DELETE_USER_PRICE_ALERT } from "../enums";
import { Dispatch } from "react";
import { httpClient } from "../../clients/auth/addTokenToHTTPRequests";
import ReactDOM from "react-dom";
import React from "react";
import NotificationToast from "../../components/common/NotificationToast";

// Helper function to show the price alert toast notification
const showPriceAlertToast = (options: { 
  itemName?: string; 
  isError?: boolean; 
  errorMessage?: string;
}) => {
  const { itemName = '', isError = false, errorMessage } = options;
  
  // Create a div to render the toast into if it doesn't exist
  let toastContainer = document.getElementById('price-alert-toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'price-alert-toast-container';
    document.body.appendChild(toastContainer);
  }

  // Render the toast into the container
  ReactDOM.render(
    React.createElement(NotificationToast, { 
      itemName, 
      isError, 
      errorMessage,
      type: 'priceAlert'
    }),
    toastContainer
  );

  // Remove the toast after it's done (5 seconds)
  setTimeout(() => {
    if (toastContainer && document.body.contains(toastContainer)) {
      ReactDOM.unmountComponentAtNode(toastContainer);
    }
  }, 5000);
};

export const getUserPriceAlerts = () => async (dispatch: Dispatch<any>) => {
  return await httpClient.instance
    .get("/user/price-alert")
    .then((res) => {
      if (res.data) {
        dispatch({
          type: GET_USER_PRICE_ALERTS,
          item_ids: createArrayOfItemIds(res.data),
          priceAlerts: res.data,
        });
      }
    })
    .catch((e) => {
      // if error save favs as empty
      console.log(e);
      dispatch({
        type: GET_USER_PRICE_ALERTS,
        favourites: [],
      });
    });
};

const createArrayOfItemIds = (data: PriceAlert[]) => {
  return data.map((alert) => alert.item.crawler_id);
};

export const addPriceAlert =
  (itemId: number, alertPrice: number, itemName?: string, itemImageUrl?: string, itemData?: ItemData) => async (dispatch: Dispatch<any>) => {
    // Create the request payload
    const payload: any = {
      item_id: itemId,
      alert_price: alertPrice,
      item_name: itemName,
      item_image_url: itemImageUrl
    };

    // If itemData is provided and has pricing information, include vendors array
    if (itemData && itemData.item_pricing && itemData.item_pricing.length > 0) {
      // Create vendors array with pricing information for all vendors
      const vendors = itemData.item_pricing.map(pricing => {
        // Create base vendor object
        const vendorObj = {
          vendor_id: pricing.vendor_id,
          price: typeof pricing.price === 'number' ? pricing.price : 0,
          volume: pricing.volume || '',
          cup_price: typeof pricing.cup_price === 'number' ? pricing.cup_price : 0,
          cup_volume: pricing.cup_volume || '100g',
          special: pricing.special || false,
          vendor_product_code: pricing.vendor_product_code || itemId.toString()
        };
        
        // Include multibuy data when it exists and is valid
        if (typeof pricing.multibuy_price === 'number' && pricing.multibuy_price > 0 && 
            typeof pricing.multibuy_quantity === 'number' && pricing.multibuy_quantity > 0) {
          // @ts-ignore - Adding properties that might not be in type definition
          vendorObj.multibuy_price = pricing.multibuy_price;
          // @ts-ignore - Adding properties that might not be in type definition
          vendorObj.multibuy_quantity = pricing.multibuy_quantity;
        }
        
        return vendorObj;
      });

      // Add vendors array to the payload
      payload.vendors = vendors;
    }

    console.log('Adding price alert with payload:', payload);

    return await httpClient.instance
      .post("/user/price-alert/", payload)
      .then(async (res) => {
        dispatch(getUserPriceAlerts());
        
        // Show toast notification on successful creation
        showPriceAlertToast({ itemName });
      })
      .catch((e) => {
        console.log(e);
        
        // Check if this is a duplicate price alert error
        if (e.response && e.response.status === 500 && 
            e.response.data && e.response.data.message === "This price alert is not unique") {
          showPriceAlertToast({ 
            itemName, 
            isError: true, 
            errorMessage: "You may already have a price alert for this item" 
          });
        }
      });
  };

export const updatePriceAlert =
  (itemId: number, alertPrice: number, itemName?: string, itemImageUrl?: string, itemData?: ItemData) => async (dispatch: Dispatch<any>) => {
    // Create the request payload
    const payload: any = {
      item_id: itemId,
      alert_price: alertPrice,
      item_name: itemName,
      item_image_url: itemImageUrl
    };

    // If itemData is provided and has pricing information, include vendors array
    if (itemData && itemData.item_pricing && itemData.item_pricing.length > 0) {
      // Create vendors array with pricing information for all vendors
      const vendors = itemData.item_pricing.map(pricing => {
        // Create base vendor object
        const vendorObj = {
          vendor_id: pricing.vendor_id,
          price: typeof pricing.price === 'number' ? pricing.price : 0,
          volume: pricing.volume || '',
          cup_price: typeof pricing.cup_price === 'number' ? pricing.cup_price : 0,
          cup_volume: pricing.cup_volume || '100g',
          special: pricing.special || false,
          vendor_product_code: pricing.vendor_product_code || itemId.toString()
        };
        
        // Include multibuy data when it exists and is valid
        if (typeof pricing.multibuy_price === 'number' && pricing.multibuy_price > 0 && 
            typeof pricing.multibuy_quantity === 'number' && pricing.multibuy_quantity > 0) {
          // @ts-ignore - Adding properties that might not be in type definition
          vendorObj.multibuy_price = pricing.multibuy_price;
          // @ts-ignore - Adding properties that might not be in type definition
          vendorObj.multibuy_quantity = pricing.multibuy_quantity;
        }
        
        return vendorObj;
      });

      // Add vendors array to the payload
      payload.vendors = vendors;
    }

    console.log('Updating price alert with payload:', payload);

    return await httpClient.instance
      .patch("/user/price-alert", payload)
      .then(async (res) => {
        dispatch(getUserPriceAlerts());
        
        // Show toast notification on successful update
        showPriceAlertToast({ itemName });
      })
      .catch((e) => {
        console.log(e);
        
        // Check if this is a duplicate price alert error
        if (e.response && e.response.status === 500 && 
            e.response.data && e.response.data.message === "This price alert is not unique") {
          showPriceAlertToast({ 
            itemName, 
            isError: true, 
            errorMessage: "You may already have a price alert for this item" 
          });
        }
      });
  };

export const deletePriceAlert =
  (itemId: number) => async (dispatch: Dispatch<any>) => {
    return await httpClient.instance
      .delete("/user/price-alert/" + itemId)
      .then((res) => {
        dispatch({
          type: DELETE_USER_PRICE_ALERT,
          deletedId: itemId,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

export const sortPriceAlert = (
  sortType: "A-Z" | "Z-A" | "AscPrice" | "DescPrice"
) => {
  return {
    type: "SORT_PRICE_ALERTS",
    sortType,
  };
};

export const prettifySortTypeIdToString = (
  sortType:
    | ""
    | "A-Z"
    | "Z-A"
    | "AscPrice"
    | "DescPrice"
    | "DescSavings"
    | "Created"
) => {
  switch (sortType) {
    case "A-Z":
      return "Name (A-Z)";
    case "Z-A":
      return "Name (Z-A)";
    case "AscPrice":
      return "Price (High to Low)";
    case "DescPrice":
      return "Price (Low to High)";
    default:
      return;
  }
};
